import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ExternalLink } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Citation - a source chip / card. Renderer for the K2 `citation` block
 * (`docs/contracts/block-registry.md`): the platform's chat `BlockRenderer`
 * passes a `citation` block's `props` straight through, so the prop names
 * match the block payload exactly.
 *
 * Block props (`{ title, url, snippet }`):
 *   - `title` - the source's display name. Required.
 *   - `url` - the source URL. Required by the block schema. Surfaced as the
 *     domain chip and makes the whole card a real link (so middle-click /
 *     cmd-click / right-click behave as users expect). A non-http(s) URL is
 *     treated as missing and the card renders inert.
 *   - `snippet` - short supporting excerpt shown under the title.
 *
 * Client-only props (never part of the streamed block): `className`,
 * `variant`, `onNavigate`, `target`.
 *
 * Two render contexts via `variant`:
 *   - `card` (default) - the full source card for a "Sources" list.
 *   - `chip` - a compact inline pill (favicon-less) for dense surfaces.
 *
 * Relationship to `CitationRow`: `CitationRow` is the richer chat-stream
 * primitive (numbered `[N]` anchor + hover popover, `index/thumbnail/badge`
 * vocabulary). `Citation` is the K2-block renderer - its props are exactly
 * the streamed `{ title, url, snippet }`, nothing more, so the runtime can
 * hand a block straight to it.
 *
 * Theming is entirely token-driven (`bg-card`, `border-border`,
 * `text-foreground`, `text-muted-foreground`, …) - no hardcoded colors.
 * SSR-safe: URL parsing is wrapped + guarded, no `window` / `document` at
 * module top.
 */

const citationVariants = cva(
  cn(
    "group/citation relative isolate flex min-w-0 flex-col gap-2 overflow-hidden text-left text-sm",
    "border border-border/70 bg-card text-card-foreground",
    "transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
  ),
  {
    variants: {
      variant: {
        // `card` is a full block surface (matches the app's surface radius +
        // inset); `chip` is a compact inline pill and keeps its small radius.
        card: "w-full max-w-md rounded-2xl p-5",
        chip: "max-w-sm flex-row items-center gap-1.5 rounded-md px-2 py-1",
      },
      interactive: {
        true: "cursor-pointer hover:border-foreground/25",
        false: "cursor-default",
      },
    },
    defaultVariants: { variant: "card", interactive: false },
  },
);

export type CitationVariant = "card" | "chip";

export interface CitationProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "title">,
    Pick<VariantProps<typeof citationVariants>, "variant"> {
  /** Source title. Required. */
  title: string;
  /** Source URL. http(s) only; anything else renders the card inert. */
  url: string;
  /** Short supporting excerpt shown under the title. */
  snippet?: string;
  /**
   * Intercept navigation (e.g. log the click, route in-app). Receives the
   * sanitized href. When omitted, the underlying `<a>` navigates natively.
   */
  onNavigate?: (url: string) => void;
  /** Anchor target. Defaults to `_blank` with `rel="noopener noreferrer"`. */
  target?: React.HTMLAttributeAnchorTarget;
}

/** Only http(s) URLs are treated as navigable - guards against `javascript:` etc. */
function sanitizeUrl(raw: string): string | null {
  try {
    const parsed = new URL(raw);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.toString();
    }
    return null;
  } catch {
    return null;
  }
}

function extractDomain(raw: string): string | undefined {
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

export const Citation = React.forwardRef<HTMLElement, CitationProps>(function Citation(
  { className, variant = "card", title, url, snippet, onNavigate, target, ...rest },
  ref,
) {
  const safeUrl = sanitizeUrl(url);
  const domain = extractDomain(url);
  const interactive = safeUrl != null;

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!onNavigate || !safeUrl) return;
      // Preserve modified clicks (new tab / save) for the native anchor.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      onNavigate(safeUrl);
    },
    [onNavigate, safeUrl],
  );

  const body =
    variant === "chip" ? (
      <>
        <ExternalLink className="size-3.5 shrink-0 opacity-60" aria-hidden />
        <span className="truncate text-muted-foreground">{domain ?? title}</span>
      </>
    ) : (
      <>
        <div className="flex min-w-0 items-center justify-between gap-1.5 text-xs text-muted-foreground">
          <span className="truncate font-medium">{domain ?? "Source"}</span>
          {interactive ? (
            <ExternalLink
              className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover/citation:opacity-100"
              aria-hidden
            />
          ) : null}
        </div>
        <h3 className="text-[15px] font-medium leading-snug text-foreground">
          <span className="line-clamp-2 group-hover/citation:underline group-hover/citation:underline-offset-2">
            {title}
          </span>
        </h3>
        {snippet ? (
          <p className="line-clamp-3 text-[13px] leading-relaxed text-muted-foreground">
            {snippet}
          </p>
        ) : null}
      </>
    );

  const classes = cn(citationVariants({ variant, interactive }), className);

  if (interactive && safeUrl) {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={safeUrl}
        target={target ?? "_blank"}
        rel={target && target !== "_blank" ? undefined : "noopener noreferrer"}
        onClick={handleClick}
        aria-label={title}
        className={cn(classes, "no-underline")}
        data-slot="citation"
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {body}
      </a>
    );
  }

  // Inert (no valid URL): render a non-link surface so the thread still shows
  // the source title + snippet instead of crashing or producing a dead link.
  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      aria-label={title}
      className={classes}
      data-slot="citation"
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
    >
      {body}
    </div>
  );
});

Citation.displayName = "Citation";

export { citationVariants };
export type CitationVariantProps = VariantProps<typeof citationVariants>;
